import React, { useEffect, useRef } from 'react';

import { Alert, AlertTitle, Stack, Typography } from '@mui/material';

import PromoResolution from '~/components/Promo/PromoResolution';

import { useGetPromoWarnings } from '~/hooks/apis/promo/useGetPromoWarnings';

import { InternalPromoWithDiscounts } from '~/services/PromoService';

import PromoWarningAction from './PromoWarningActions';

type Promo = Pick<InternalPromoWithDiscounts, 'code_name' | 'brand' | 'id_promo_code'>;
type Props = { promo?: Promo | null };

export default function PromoDetailWarnings({ promo }: Props) {
  const { data, isLoading, error, refetch } = useGetPromoWarnings({
    codeName: promo?.id_promo_code ? promo.code_name : '',
    brand: promo?.brand,
  });
  const previousPromo = useRef(promo);

  useEffect(() => {
    // Refresh server checks when a save or child edit reloads the same promo.
    if (
      promo?.id_promo_code &&
      previousPromo.current !== promo &&
      previousPromo.current?.id_promo_code === promo.id_promo_code
    ) {
      void refetch();
    }
    previousPromo.current = promo;
  }, [promo, refetch]);

  return (
    <Stack spacing={2}>
      {promo?.id_promo_code && isLoading && <Typography variant="body2">Loading full promo warnings…</Typography>}
      {error && <Alert severity="error">Could not load full promo warnings: {error.message}</Alert>}
      {(data ?? []).map((warning, index) => (
        <Alert key={`${warning.code}-${index}`} severity={warning.level} title={JSON.stringify(warning, null, 4)}>
          <AlertTitle>{warning.code}</AlertTitle>
          {warning.message || 'Please review this warning before proceeding.'}
          <PromoResolution resolution={warning.resolution} />
          <PromoWarningAction promoWarning={warning} promoCodeName={promo?.code_name} />
        </Alert>
      ))}
    </Stack>
  );
}
